/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * Bridge is the interface between the user interface managed by the Elm Main component and the supporting Solution and
 * Rest libraries. It holds the current authentication.
 *
 * After initializing the user interface (Elm.Main.init), it waits for library requests from the user interface
 * (this._elmComponent.ports.askJSLibrary.subscribe) and returns responses from the library(ies) to the UI
 * (this.elmCallback, this._elmComponent.ports.elmCallback.send).
 *
 * @module Bridge
 */

//#region Interfaces

import * as cardsFetcher from "./CardsFetcher";
import * as cardsHelpers from "./cardsHelpers";
import * as common from "@esri/solution-common";
import * as deployer from "@esri/solution-deployer";
import * as helpers from "./helpers";
import * as portal from "@esri/arcgis-rest-portal";
import * as request from "@esri/arcgis-rest-request";
import * as restTypes from "@esri/arcgis-rest-types";
import { Auth } from "./Auth";
import { Elm } from "./Main";
import { ISolutionGraphTranslations, ITemplateInfoGraph } from "./solutionGraph";
import { Logger, LogLevel } from "./Logger";
import { prepareDataForSolutionDisplay } from "./prepareDataForSolutionDisplay";
import { SolutionDisplay } from "./SolutionDisplay";
import { Telemetry } from "src";

export interface ISolutionItemHover {
  itemId: string;
  entering: boolean;
}

export interface ISolutionItemSelected {
  itemId: string;
  selected: boolean;
}

export interface ISolutionTypeHover {
  itemType: string;
  entering: boolean;
}

export { ISolutionInfoCard } from "./cardsHelpers";

enum ActiveProcess {
  Deployment = "DEPLOYMENT",
  Deletion = "DELETION"
}

export interface IAppConfig {
  appId: string;
  primarySolutionsGroupId: string; // id of group that's the primary source of Solutions for AGO or Enterprise
  agoBasedEnterpriseSolutionsGroupId: string; // id of an AGO-based set of Enterprise Solutions
  portalURL: string;
  locale: string;
  isRTL: boolean;
  pageTranslations: string[];
  typescriptTranslations: common.IStringValuePair; // JSON object with hierarchy of key:languageString pairs for a particular language
  elmTranslations: common.IStringValuePair; // JSON object with hierarchy of key:languageString pairs for a particular language
  overrideLocale: string; // locale override via URL, adjusted to language available to app
  inGalleryMode: boolean; // displays UI in gallery mode
  inTestingMode: boolean; // displays UI without signin
  measurementIds: string[]; // used for storage of analytics
  cookiesFirstUseVar: string; // used for storage of analytics
  trackingEnabled: boolean; // when true analytics tracking will be enabled
  forceTrackingEnv: TrackingEnv; // when this value is set it will override document and portal checking for tracking
  logger: Logger;
  telemetry: TelemetryOptions;
  suppress_solutionDiagramFeature_announcement?: boolean;
}

export interface TelemetryOptions {
  name: string;
  version: string;
  dev: AmazonTelemetryOptions;
  prod: AmazonTelemetryOptions;
  qa: AmazonTelemetryOptions;
}

interface AmazonTelemetryOptions {
  amazon: AmazonEnvTelemetryOptions;
}

interface AmazonEnvTelemetryOptions {
  userPoolID: string;
  app: AmazonTelemetryAppOptions;
}

interface AmazonTelemetryAppOptions {
  id: string;
}

interface IElmCallback {
  command: string;
  data: any;
  optionalData: any;
}

interface IRequestDeploySolution {
  card: cardsHelpers.ISolutionInfoCard;
  reuse: boolean;
  jobId: string;
  userOptions: IUserOption[];
}

export interface ISolutionIDs {
  ids: string[];
}

export interface ISpatialReferences {
  wkids: helpers.ISpatialReference[];
}

export interface IUserOption {
  id: string;
  props: IUserProp[];
  accordionDisplay: boolean;
}

export interface IUserProp {
  autoSelect: boolean,
  id: string,
  itemType: string,
  label: string,
  prop: string,
  default: string,
  value: string,
  responseValue: string,
  choices: string[],
  valueType: string,
  attributes: any,
  validation: any,
  valid: boolean,
  validId: boolean,
  srSearchText: string,
  outType: string,
  loading: boolean,
  tooltip: string,
  name: string,
  title: string,
  itemUrl: string,
  existingItemId: string
}

export type TrackingEnv = "dev" | "qa" | "prod" | "";

//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

export class Bridge {

  private _appConfig: IAppConfig;
  private _elmComponent: Elm.Main.App | null = null;
  private _portalUrl: string;
  private _orgUrl: string;
  private _authentication: Auth | null = null;
  private _cardsFetcherAvailable: cardsFetcher.CardsFetcher;
  private _cardsFetcherDeployed: cardsFetcher.CardsFetcher;
  private _userSession: common.UserSession | undefined;
  private _anonAgolSession: common.UserSession | undefined;
  private _activeDeploymentCount = 0;
  private _activeDeletionCount = 0;
  private _spatialReferences: ISpatialReferences;
  private _regexFsUrl: string = "https?:\\/\\/.+FeatureServer\\b";
  private _regexID: string = "\\b[0-9A-F]{32}\\b";
  private _customDeploymentMappings: common.IStringValuePair = {};
  private _configureLearnMore: string = "";
  private _currentTrackingQuery: string = "";
  private _currentTrackingIndustry: string = "";
  private _currentTrackingIndustryChanged: boolean = false;;
  private _currentTrackingDomain: string = "";
  private _currentTrackingDomainChanged: boolean = false;
  private _currentTrackingId: string = "";
  private _currentTrackingVersion: string = "";
  private _currentTrackingNewRelease: boolean = false;
  private _currentTrackingSuggestedForYou: boolean = false;
  private _customLocationTrackingAdminAndTracking: string = "solutions:locationtracking:admin:tracking";
  private _customLocationTrackingTracking: string = "solutions:locationtracking:tracking";
  private _customLocationTrackingAdmin: string = "solutions:locationtracking:admin";
  private _customHubPremium: string = "solutions:hubPremium";
  private _customVelocity: string = "solutions:velocity";
  private _customNotebookServer: string = "solutions:notebook";
  private _relatedSolutions: Set<string> = new Set();
  private _homePageNeedsRefresh = true;
  private _activeScrollingMetering: any = null;
  private _allCards: cardsHelpers.ISolutionInfoCard[] = [];
  private _solutionIdMappingToItemId: cardsHelpers.IKeyedStrings = {};
  // When true the app can enable Telemetry
  // This value is set based on the setting in the appConfig
  private _trackingEnabled = false;
  // When true events will be tracked and messages will be written based on appConfigs measurement ids
  private _logTelemetryEvents = false;

  private _forceTrackingEnv: TrackingEnv = "";
  private _telemetryInstance: Telemetry | undefined;
  private _trackingDidDeploy = false;
  private _trackingIsConfigure = false;
  private _metadataTitleSuffix = " - ArcGIS Solutions";
  private _solutionDisplay: SolutionDisplay | undefined;

  // ---------------------------------------------------------------------------------------------------------------- //
  //#region Elm interface
  public constructor(
    appConfig: IAppConfig,
    uiContainer: HTMLElement,
    authentication: Auth,
    spatialReferences: ISpatialReferences,
  ) {
    this._appConfig = appConfig;
    this._authentication = authentication;
    this._userSession = authentication.RestUserSession ?? undefined;
    this._trackingEnabled = appConfig.trackingEnabled;
    this._forceTrackingEnv = appConfig.forceTrackingEnv;
    this._portalUrl = authentication.PortalUrl;
    this._orgUrl = authentication?.PortalSubset?.orgUrl || this._portalUrl;
    this._spatialReferences = spatialReferences;

    this._cardsFetcherAvailable = new cardsFetcher.CardsFetcher(this._appConfig.locale, this._orgUrl, this._portalUrl);
    this._cardsFetcherDeployed = new cardsFetcher.CardsFetcher(this._appConfig.locale, this._orgUrl, this._portalUrl);

    void this.launch(uiContainer);
  }

  /**
   * Launches the UI.
   *
   * @param uiContainer HTML div element to contain the Elm UI
   */
  private async launch(
    uiContainer: HTMLElement,
  ): Promise<void> {

    // Update the generic page description using the current language
    this._setMetadataForGallery(this._appConfig.pageTranslations[2]);

    // Set up anonymous arcgis online user session for query to public enterprise connected apps group
    const anonPortal = helpers.getAnonymousPortalUrl(this._portalUrl);
    this._anonAgolSession = new common.UserSession({
      clientId: this._appConfig.appId,
      portal: `${anonPortal}/sharing/rest`
    });

    let templateSearchTimer: any;
    let deployedSearchTimer: any;

    // There's no point in showing the My Solutions page if the user is not signed in
    let pageUrl = window.location.href;
    if (!this._authentication?.SignedIn) {
      pageUrl = pageUrl.replace("#solutions", "#home");
    }

    if (this._trackingEnabled) {
      this._handleConsentManager(this._authentication, this._appConfig);
    }

    // Init the available cards cache, which includes the mappings from solution ids to
    // item ids in the available solutions cache

    const fetchAvailableSolns = await this._cardsFetcherAvailable.requestAvailableSolutionCards(
      {
        "q": "(typekeywords:Solution,Template) OR (typekeywords:Solution,Display Only)",
        "filter": "type:Solution",
        "sortField": "title",
        "sortOrder": "asc",
        "nextStart": -1,
        "categories": [],
        "delay": false,
        "getSuggested": false
      } as cardsFetcher.ISearchOptionPartial,
      this._appConfig.primarySolutionsGroupId,
      this._appConfig.agoBasedEnterpriseSolutionsGroupId,
      this._userSession,
      {
        "num": 1000
      } as restTypes.IPagingParams
    );

    this._allCards = fetchAvailableSolns.cards;

    // Prepare the mappings from solution ids to item ids
    this._solutionIdMappingToItemId = this.buildSolutionIdMappingToItemId(this._allCards);

    // Convert the solution ids in all of the response cards
    this._allCards = this._allCards.map(
      card => {
        if (Array.isArray(card.relatedSolutions) && card.relatedSolutions.length > 0) {
          card.relatedSolutions =
            cardsHelpers.convertSolutionIdsToItemIds(card.relatedSolutions, this._solutionIdMappingToItemId);
        }
        return card;
      }
    );

    // Get the deployed cards as well because we need to be prepared to support suggested solutions
    if (this._authentication?.SignedIn) {
      const deployedSolnsQuery: cardsFetcher.ISearchOptionPartial = {
        "q": "((typekeywords:Solution,Deployed,'ArcGIS Solution') OR ((typekeywords:Solution,Deployed) AND (created:[1577862000000 TO 1622576880000])))",
        "filter": "type:Solution",
        "sortField": "created",
        "sortOrder": "desc",
        "delay": false,
        "categories": []
      }
      deployedSolnsQuery.q += this.getOrgAndOwnerQueryPart(this._authentication);

      await this.requestDeployedSolutionCards("", deployedSolnsQuery);
    }

    // See if we have any related solutions
    this._relatedSolutions = cardsHelpers.getDeployedRelatedSolutionIds(this._cardsFetcherDeployed.getCache(), this._allCards);

    // Gather the user privileges
    let userPrivileges: string[] = [];
    if (this._userSession) {
      // Get the user's privileges and forward them to the Elm module
      userPrivileges = this._authentication?.PortalUserPrivileges
        ? await this.getUserPrivileges(this._authentication?.PortalUserPrivileges)
        : [];

      // Refresh the token near the end of the expiration period
      const expiration = this._userSession.tokenExpires.getTime();
      const refreshInterval = (expiration - Date.now()) * 0.9;
      setInterval(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async () => {
          this._userSession = (await this._authentication?.refreshSession()) ?? undefined;
          const token = this._userSession?.token ?? "";
          this.elmCallback("IOPCmdUpdateToken", token);
        }, refreshInterval
      );
    }

    // Init Elm library in a subsidiary div because it takes over the div
    const elmContainer = document.createElement("div");
    uiContainer.appendChild(elmContainer);

    this._elmComponent = Elm.Main.init({
      node: elmContainer,
      flags: {
        pageUrl,
        translations: this._appConfig.elmTranslations,
        overrideLocale: this._appConfig.overrideLocale,
        isRTL: this._appConfig.isRTL,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        gallery: this._appConfig.inGalleryMode,
        testing: this._appConfig.inTestingMode,
        isEnterprise: this._authentication?.IsEnterprisePortal,
        isReadOnly: this._authentication?.PortalSubset
          ? this._authentication?.PortalSubset?.isReadOnly : true,
        token: this._userSession?.token ?? "",
        allTemplates: this._allCards,
        hasRelatedSolutions: this._relatedSolutions.size > 0,
        userPrivileges,
        suppress_solutionDiagramFeature_announcement: this._appConfig.suppress_solutionDiagramFeature_announcement
      }
    });

    const elmRequestHandler =
      (query: IElmCallback) => {
        switch (query.command) {

          case "IOPCmdRequestAvailableSolutions":
            const availSolnsQuery = query.data as cardsFetcher.ISearchOptionPartial;

            const pagingParams: portal.IPagingParams | undefined = availSolnsQuery.nextStart > 0
              ? this._appConfig.inGalleryMode
                ? {
                    start: availSolnsQuery.nextStart,
                    num: 24
                  }
                : {
                    start: 1,
                    num: availSolnsQuery.nextStart + 23 // 24 per page, but set back from nextStart--the first one of the next batch
                  }
              : undefined;

            const availableSolutionsRequest = () => this.requestAvailableSolutionCards(
              query.command,
              availSolnsQuery,
              this._appConfig.primarySolutionsGroupId,
              this._appConfig.agoBasedEnterpriseSolutionsGroupId,
              pagingParams);


            clearTimeout(templateSearchTimer);
            if (availSolnsQuery.delay) {
              templateSearchTimer = setTimeout(availableSolutionsRequest, 1000);
            }
            else {
              availableSolutionsRequest();
            }
            break;

          case "IOPCmdRequestDeployedSolutions":
            const deployedSolnsQuery = query.data as cardsFetcher.ISearchOptionPartial;
            if (this._authentication) {
              deployedSolnsQuery.q += this.getOrgAndOwnerQueryPart(this._authentication);

              const deployedSolutionsRequest = async () => this.requestDeployedSolutionCards(
                query.command,
                deployedSolnsQuery);


              clearTimeout(deployedSearchTimer);
              if (deployedSolnsQuery.delay) {
                deployedSearchTimer = setTimeout(() => void deployedSolutionsRequest(), 1000);
              }
              else {
                void deployedSolutionsRequest();
              }
            } else {
              this.elmCallback(query.command, []);
            }
            break;

          case "IOPCmdRequestSelectedSolution":
            const selectedSolnsQuery = query.data as cardsFetcher.ISearchOptionPartial;

            this.requestAvailableSolutionCards(
              query.command,
              selectedSolnsQuery,
              this._appConfig.primarySolutionsGroupId,
              this._appConfig.agoBasedEnterpriseSolutionsGroupId
            );
            break;

          case "IOPCmdGenerateShareLinkForSoln":
            this.placeShareLinkForSolnOntoClipboard();
            break;

          case "IOPCmdRequestToDeploySolution":
            if (this._userSession) {
              self.navSelectByName("solutions");
              this.elmCallback("IOPCmdDeployingStarted", []);
              this.deploySolution(query.command, query.data as IRequestDeploySolution, this._userSession);
            }
            break;

          case "IOPCmdRequestToDeploySolutionAtLaunch":
            if (this._userSession) {
              // Provide a job id for the deployment
              this.elmCallback("IOPCmdRequestDeployJobId", common.createLongId());

              const card = query.data.card as cardsHelpers.ISolutionInfoCard;

              const cardSession = cardsHelpers.getCardSession(card, this._userSession, this._anonAgolSession);
              if (card && cardSession) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                common.getItemDataAsJson(card.id, cardSession).then(
                  (solutionData: common.ISolutionItemData) => {
                    const templateIdList = solutionData.templates.map(template => template.itemId);
                    const templateTypeList = solutionData.templates.map(template => template.type);

                    const privilegeList = this.getRequiredSolutionPrivileges(templateTypeList);
                    const templateTypeKeywords = solutionData.templates.reduce(
                      (prev: string[], cur: common.IItemTemplate) => {
                        return prev.concat(cur.item.typeKeywords || []);
                      }, []);
                    this._updatePrivilegeListByTemplateTypeKeywords(privilegeList, templateTypeKeywords);
                    this._updatePrivilegeListBySolutionTypeKeywords(privilegeList, card.typeKeywords);
                    this._updatePrivilegeListByGroup(privilegeList, solutionData);
                    const userOptionsDef = solutionData.params ? this.formatParams(solutionData.params) : Promise.resolve([]);
                    void userOptionsDef.then(userOptions => {
                      // Deploy the solution described by the supplied card
                      card.templateInfos = {
                        "ids": templateIdList,
                        "privileges": this._userSession?.username ? privilegeList : [],
                        "userOptions": userOptions,
                        "configureLearnMore": this._configureLearnMore
                      }
                      this.elmCallback("IOPCmdRequestToDeploySolutionAtLaunch", card);
                    });
                  }
                )
              }
            }
            break;

          case "IOPCmdOpenModal":
            requestAnimationFrame(() => {
              this._customDeploymentMappings = {};
              const card: cardsHelpers.ISolutionInfoCard = query.data as cardsHelpers.ISolutionInfoCard;

              const cardSession = cardsHelpers.getCardSession(card, this._userSession, this._anonAgolSession);
              if (card && cardSession) {
                if (this._logTelemetryEvents) {
                  this._currentTrackingId = card.id;
                  this._currentTrackingVersion = card.deployVersion;
                }
                common.getItemDataAsJson(card.id, cardSession).then(
                  (solutionData: common.ISolutionItemData) => {
                    const templateIdList = solutionData.templates.map(template => template.itemId);
                    const templateTypeList = solutionData.templates.map(template => template.type);

                    const privilegeList = this.getRequiredSolutionPrivileges(templateTypeList);
                    const templateTypeKeywords = solutionData.templates.reduce(
                      (prev: string[], cur: common.IItemTemplate) => {
                        return prev.concat(cur.item.typeKeywords || []);
                      }, []);
                    this._updatePrivilegeListByTemplateTypeKeywords(privilegeList, templateTypeKeywords);
                    this._updatePrivilegeListBySolutionTypeKeywords(privilegeList, card.typeKeywords);
                    this._updatePrivilegeListByGroup(privilegeList, solutionData);
                    const userOptionsDef = solutionData.params ? this.formatParams(solutionData.params) : Promise.resolve([]);
                    void userOptionsDef.then(userOptions => {
                      this.elmCallback(
                        "IOPCmdSetActiveCardTemplateInfos",
                        {
                          "ids": templateIdList,
                          "privileges":  this._userSession?.username ? privilegeList : [],
                          "userOptions": userOptions,
                          "configureLearnMore": this._configureLearnMore
                        }
                      );
                    });

                    // Show the modal
                    this.focusModalContent(modal);

                    this._logEvent(
                      "solution_open_modal",
                      card.title,
                      `Solution ID: ${card.id}|Version: ${card.deployVersion}`
                    );

                    this._selectOverviewTab();
                    this._updateWhatsIncludedTab(solutionData.templates);
                  }
                  , error => {
                    this._appConfig.logger.log(LogLevel.Errors, error);
                  }
                );
              }

              // Set the page metadata for this Solution
              if (card) {
                this._setMetadataForSolution(card.title, card.snippet, card.thumbnailUrl);
              }
            });
            break;

          case "IOPCmdToTriggerMeteredScroll":
            if (!this._activeScrollingMetering) {
              this._activeScrollingMetering = setTimeout(
                () => {
                  // Rounding up for issue 435 scrolling in chrome on android and add a bit to the offsetHeight
                  // to trigger new batch when we're closer to the end without actually being there.
                  // Also, for issue 864, throttle scrolling so that Elm has time to update the DOM. Ignore any
                  // scroll events while we're handling one. Part of this bug was that the DOM wasn't getting
                  // fully updated during the rapid-fire scroll events, and so the scroll height property was
                  // wildly wrong--too small--thus triggering yet another refresh as if the user had scrolled
                  // down some more.
                  const resultSection = document.getElementById("results-section");
                  if (resultSection &&
                    (resultSection.scrollHeight - resultSection.scrollTop) <= (resultSection.offsetHeight + 80)) {
                    this.elmCallback("IOPCmdToTriggerScrollRefresh");
                  }
                  this._activeScrollingMetering = null;
                }, 500
              );
            }
            break;

          case "IOPCmdOpenDeleteModal":
            // Check if a deletion is possible
            if (this._authentication?.PortalSubset?.isReadOnly) {
              // Create alert
              const warnBeforeDeletion = document.createElement("calcite-alert");
              warnBeforeDeletion.id = "cannotDelete";
              warnBeforeDeletion.icon = "x-circle";
              warnBeforeDeletion.kind = "danger";
              warnBeforeDeletion.placement = "bottom";
              warnBeforeDeletion.scale = "m";
              warnBeforeDeletion.messageOverrides = {
                close: helpers.getTranslation(this._appConfig.typescriptTranslations, "buttons.close")
              };

              const alertMessage = document.createElement("div");
              alertMessage.setAttribute("slot", "message");
              const translationRoot = this._appConfig.elmTranslations.cards.missingPrivilegesPopup.requiredPrivilegesList;
              alertMessage.innerHTML = translationRoot[this._authentication.IsEnterprisePortal ? "writableEnterprise" : "writableOnline"];
              warnBeforeDeletion.appendChild(alertMessage);

              warnBeforeDeletion.addEventListener("calciteAlertClose",
                () => {
                  document.body.removeChild(warnBeforeDeletion);
                }
              );

              // Show the alert
              document.body.appendChild(warnBeforeDeletion);
              warnBeforeDeletion.open = true;

              // Cancel the request to open the delete modal
              this.elmCallback("IOPCmdCloseModal");

            // Present a confirmation display that includees a list of the related items also to be deleted
            } else {
              requestAnimationFrame(() => {
                const card: cardsHelpers.ISolutionInfoCard = query.data;

                if (card && this._userSession) {
                  common.getDeletableSolutionInfo(card.id, this._userSession).then(
                    (solutionData: common.ISolutionPrecis) => {
                      const relatedItems: any = solutionData.items.map(template => {
                        const partialItem = {
                          "itemName": template.title,
                          "itemType": template.type,
                          "owner": template.owner,
                          "modified": helpers.getLocaleLongDateString(
                            template.modified,
                            this._appConfig.locale
                          )
                        };
                        return partialItem;
                      });
                      this.elmCallback("IOPCmdSetDeleteCardRelatedItems", relatedItems);
                    }
                    , error => {
                      this._appConfig.logger.log(LogLevel.Errors, error);
                    }
                  );
                }
                self.navSelectByName("solutions");
                this.focusModalContent(modal, "title");
              });
            }
            break;

          case "IOPCmdOpenDiagramModal":
            // Clean up the solution diagram if it exists; placed here because Calcite Modal doesn't appear to support
            // its calciteModalClose event
            if (this._solutionDisplay) {
              this._solutionDisplay.dispose();
              this._solutionDisplay = undefined;
            }

            // Create a new solution diagram
            const card: cardsHelpers.ISolutionInfoCard = query.data;
            if (card && this._userSession) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              prepareDataForSolutionDisplay(card.id, this._userSession).then(
                () => {
                  requestAnimationFrame(() => {
                    self.navSelectByName("solutions");
                    this.focusModalContent(modal, "title");
                    this.elmCallback(query.command, card);
                  });
                }
              );
            }
            break;

          case "IOPCmdCloseAlert":
            const alertName = query.data.name;
            const alert = document.getElementById(alertName);
            if (alert) {
              alert.setAttribute("open", "false");
            }
            break;

          case "IOPCmdSetCookie":
            const cookie = query.data;
            document.cookie = `${cookie.name}=${cookie.value}; max-age=${cookie.maxAge}`;
            break;

          case "IOPCmdOpenFilterModal":
            requestAnimationFrame(() => {
              self.navSelectByName("home");
              this.focusModalContent(modal, "title");
            });
          break;

          case "IOPCmdOpenLink":
            const link = query.data as string;
            window.open(link, "_blank");
            break;

          case "IOPCmdPopulateDiagramModal":
            const diagramCard: cardsHelpers.ISolutionInfoCard = query.data;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._drawSolutionDiagram(diagramCard).then(
              (solutionDisplay: SolutionDisplay | undefined) => {
                this._solutionDisplay = solutionDisplay;
              }
            );
            break;

          case "IOPCmdTriggerFocus":
            if (!isDragging) {
              const cardId = query.data as string;
              const card = document.getElementById("solution-card-id-" + cardId) as HTMLElement;
              const cardChild = card.firstElementChild as HTMLElement;
              cardChild.focus();
              cardChild.classList.add("menu-focus");
              this.elmCallback(query.command, cardId);
            }
            break;

          case "IOPCmdTriggerBlur":
            const cardBlurId = query.data as string;
            const eventObj = query.optionalData; // event obj passed from elm
            const cardBlur = document.getElementById("solution-card-id-" + cardBlurId) as HTMLElement;
            if (!cardBlur.contains(eventObj.relatedTarget as Node)) {
              cardBlur.firstElementChild?.classList.remove("menu-focus");
              this.elmCallback(query.command, cardBlurId);
            }
            break;

          case "IOPCmdRequestToDeleteSolution":
            requestAnimationFrame(() => {
              const card: cardsHelpers.ISolutionInfoCard = query.data;

              if (card && this._userSession) {
                this.addActiveProcess(ActiveProcess.Deletion);
                const fallbackItemList: any = [{
                  "itemName": card.title,
                  "itemType": "Solution",
                  "owner": this._authentication?.PortalUserName,
                  "modified": card.modified
                }];
                // Delete a solution described by the supplied id
                const options: common.IDeleteSolutionOptions = {
                  progressCallback: (percentDone) => {  // progress callback
                    this.elmCallback("IOPNotifyDeleteUpdate", Math.round(percentDone));
                  },
                };
                common.deleteSolution(card.id, this._userSession, options).then(
                  (deletes: common.ISolutionPrecis[]) => {
                    // const successSummary: common.ISolutionPrecis = deletes[0];
                    const failedSummary: common.ISolutionPrecis = deletes[1];
                    let failedDeletes: any;
                    if (failedSummary) {
                      failedDeletes = failedSummary.items.map(template => {
                        const partialItem = {
                          "itemName": template.title,
                          "itemType": template.type,
                          "owner": template.owner,
                          "modified": helpers.getLocaleLongDateString(
                            template.modified,
                            this._appConfig.locale
                          )
                        };
                        this._logEvent(
                          "solution_delete_template_failed",
                          template.title,
                          `Solution ID: ${card.id}|Version: ${card.deployVersion}`
                        );
                        return partialItem;
                      });
                    }
                    else {
                      // Rare Case that solution item itself doesn't delete
                      failedDeletes = fallbackItemList;
                    }
                    this.removeActiveProcess(ActiveProcess.Deletion);
                    this.elmCallback("IOPCmdRequestToDeleteSolution", failedDeletes);
                    if (failedDeletes.length === 0) {
                      this.createAlert("success", card, ActiveProcess.Deletion);
                    }
                    // reset delete progressor
                    this.elmCallback("IOPNotifyDeleteUpdate", 0.0);

                    // Update the list of related solutions after removing the deleted solution from the cache
                    this._cardsFetcherDeployed.removeFromCache(card.id);
                    this._relatedSolutions = cardsHelpers.getDeployedRelatedSolutionIds(this._cardsFetcherDeployed.getCache(), this._allCards);
                    this.elmCallback("IOPCmdUpdateRelatedSolutions", null, this._relatedSolutions.size > 0);
                  }
                  , error => {
                    this.removeActiveProcess(ActiveProcess.Deletion);
                    this.elmCallback("IOPCmdRequestToDeleteSolution", fallbackItemList);

                    // reset delete progressor
                    this.elmCallback("IOPNotifyDeleteUpdate", 0.0);
                    this._appConfig.logger.log(LogLevel.Errors, error);

                    this._logEvent(
                      `solution_delete_failed_${card.title}`,
                      JSON.stringify(error),
                      `Solution ID: ${card.id}|Version: ${card.deployVersion}`
                    );
                  }
                );
              }
              this.focusModalContent(modal);
            });
            break;

          case "IOPCmdCloseModal":
            if (query.data === "askJSToDeploySolution" && this._logTelemetryEvents) {
              this._trackingDidDeploy = true;
            }
            modal.open = false;

            // Closing the Solutions details modal; restore the generic page metadata
            this._setMetadataForGallery(this._appConfig.pageTranslations[2]);

            // Cleanup share structure
            const copyToClipboardLink = document.getElementById("copyToClipboardLink");
            if (copyToClipboardLink) {
              document.body.removeChild(copyToClipboardLink);
            }
            const copyToClipboardTextarea = document.getElementById("copyToClipboardTextarea");
            if (copyToClipboardTextarea) {
              document.body.removeChild(copyToClipboardTextarea);
            }
            const shareBtn = document.getElementById("shareLinkToSolutionBtn") as Element;
            if (shareBtn) {
              (shareBtn as HTMLInputElement).disabled = false;
            }

            // Clean up the solution diagram if it exists
            if (this._solutionDisplay) {
              this._solutionDisplay.dispose();
              this._solutionDisplay = undefined;
            }
            break;

          case "IOPCmdValidateID":
            this.validateID(query.data);
            break;

          case "IOPCmdGetValueById":
            // Hack function to get color as the calciteColorChange event emits void
            // also represents a simple JS check of a value based on ID for any of the controls
            let v = "";
            const id: string = common.getProp(query, "data.id");
            if (id) {
              const elm: any = document.getElementById(id);
              if (elm) {
                v = elm.value;
              }
            }
            this.elmCallback("IOPCmdGetValueById", v, id);
            break;

          case "IOPCmdUpdateModal":
            const inData = query.data;
            const inInfoMode: string = inData.infoMode;

            requestAnimationFrame(() => {
              const secondaryButton = document.querySelector("calcite-button[slot='secondary']") as HTMLElement;
              secondaryButton?.blur();

              if (inInfoMode === "configure") {
                if (this._logTelemetryEvents) {
                  this._trackingIsConfigure = true;
                }

                const srInput: any = document.getElementById("SpatialReferenceControl");
                if (srInput) {
                  srInput.hideCategories = ["Vertical Coordinate Systems"];
                }
              }
            });

            const modalTitle = document.getElementById("solutions-modal-title") as HTMLElement;
            // Add Tab Index so content is focusable
            modalTitle.tabIndex = 0;
            // Remove outline because we dont want a black box
            // Inline styling because element gets created in shadow root
            modalTitle.style.outline = "none";
            modal.open = true;
            void modal.setFocus();
            break;

          case "IOPCmdUpdateUrlSearch":
            // Replace the URL search params (query.data includes the hash), which will trigger a page refresh
            window.location.href = window.origin + window.location.pathname + query.data;
            break;

          case "IOPCmdUpdateVisibleOptions":
            const data: any = query.data;
            if (typeof data.index === "number") {
              const i = data.index;
              const tabs = Array.from(document.getElementsByClassName("configure-tab"));
              tabs.forEach((t, index) => {
                t.classList.add(index === i ? "display-block" : "display-none");
                t.classList.remove(index === i ? "display-none" : "display-block");
              });
            }
            break;

          case "IOPCmdNavToggle":
            if (query.data === "home") {
              homeHeaders.forEach(function (el) {
                self.navSelectByElement(el);
              });
              self._setActiveTertiaryMenuTab(true);
            }
            else {
              mySolutionsHeaders.forEach(function (el) {
                self.navSelectByElement(el);
              });
              self._setActiveTertiaryMenuTab(false);
            }
            break;

          case "IOPCmdUrlChange":
            // Change the URL upon request, inform app of the change.
            const url = query.data as string;

            window.history.replaceState({}, '', url);
            break;

          case "IOPCmdUpdateFilterMetatags":
            const title = query.data ? (query.data as string) : undefined;
            this._setMetadataForGallery(this._appConfig.pageTranslations[2], title);
            break;

          case "IOPCmdRequestMaxHeights":
            this.getMaxHeights();
            break;

          case "IOPCmdRequestDeployJobId":
            const newGuid = common.createLongId();
            this.elmCallback(query.command, newGuid);
            break;
        }
        setTimeout(() => this.getMaxHeights(), 1000);
      }

    // Dispatch information from Elm module to libraries
    this._elmComponent?.ports.askJSLibrary.subscribe(elmRequestHandler);

    // Load deployed solutions
    if (this._userSession) {
      this.elmCallback("IOPLoadDeployedSolutions");
    }

    // Inform app of browser navigation (the BACK and FORWARD buttons)
    const self = this;
    window.addEventListener('popstate', function () {
      self.elmCallback("IOPCmdUrlChange", window.location.href);
    });

    window.addEventListener("beforeunload", (e) => {
      const activeProcesses = self._activeDeletionCount + self._activeDeploymentCount;
      if (activeProcesses > 0) {
        e.preventDefault();
      }
    });

    let isDragging = false;

    document.body.addEventListener("touchmove", () => { isDragging = true; });
    document.body.addEventListener("touchstart", () => { isDragging = false; });

    const modal = document.querySelector("calcite-modal") as HTMLCalciteModalElement;
    modal.beforeClose = () => {
      return new Promise((resolve) => {
        this._selectOverviewTab();
        modal.blur();

        if (this._logTelemetryEvents) {
          this._trackingHandleModalClose();
        }

        self.elmCallback("IOPCmdCloseModal");

        resolve();
      });
    };

    document.addEventListener("calciteTreeSelect", ((event: CustomEvent) => self.treeSelect(event)) as EventListener);

    const mySolutionsHeaders: NodeListOf<HTMLElement> = document.querySelectorAll("[data-menu-id='tab-2']");
    const homeHeaders: NodeListOf<HTMLElement> = document.querySelectorAll("[data-menu-id='tab-1']");

    // Functions for opening and closing Home and My Solutions Div
    function navSelectFcn(clickedNode: HTMLElement, tabName: string, evt?: Event): void {
      if (!evt || (evt as KeyboardEvent).code === "Enter") {
        self.navSelectByElement(clickedNode);
        self._setActiveTertiaryMenuTab(tabName === "home");
      }
    }

    mySolutionsHeaders.forEach(function (node) {
      node.addEventListener("click", () => navSelectFcn(node, "solutions"));
      node.addEventListener("keydown", (evt) => navSelectFcn(node, "solutions", evt));
    });

    homeHeaders.forEach(function (node) {
      node.addEventListener("click", () => navSelectFcn(node, "home"));
      node.addEventListener("keydown", (evt) => navSelectFcn(node, "home", evt));
    });
  }

  /**
   * Draws the solution diagram
   *
   * @param card The solution card
   * @returns Promise resolving to the solution display object
   */
  private async _drawSolutionDiagram(
    card: cardsHelpers.ISolutionInfoCard
  ): Promise<SolutionDisplay | undefined> {
    let solutionDisplay: SolutionDisplay | undefined;
    if (card && this._userSession) {
      // Get the items in the Solution
      const collection: ITemplateInfoGraph[] = await prepareDataForSolutionDisplay(card.id, this._userSession);

      // Draw the solution
      const indexPanel = document.getElementById("indexPanel") as HTMLCalciteShellPanelElement;
      const diagramPanel = document.getElementById("diagramPanel") as HTMLCalcitePanelElement;
      const diagramTranslations = this._appConfig.typescriptTranslations.solutionDiagram as ISolutionGraphTranslations;
      if (indexPanel && diagramPanel) {
        solutionDisplay = new SolutionDisplay(indexPanel, diagramPanel, diagramTranslations);
        solutionDisplay.create(collection);
      }
    }
    return solutionDisplay;
  }

  /**
   * Select the overview tab
   */
  private _selectOverviewTab(): void {
    const overviewTabTittle = document.getElementById("solution-overview") as HTMLCalciteTabTitleElement;
    if (overviewTabTittle) {
      overviewTabTittle.selected = true;
    }
  }

  /**
   * Adds the solution item accordion to the whats included tab
   */
  private _updateWhatsIncludedTab(
    templates: common.IItemTemplate[]
  ): void {
    const tab = document.getElementById("solution-whats-included-tab") as HTMLCalciteTabElement;
    tab.innerHTML = "";
    const templateInfos = templates.map(t => {
      return {
        id: t.itemId,
        type: t.type,
        title: t.item.title,
        typeKeywords: t.item.typeKeywords,
        snippet: t.item.snippet
      };
    });
    const solutionItemAccordion = document.createElement("solution-item-accordion") as any;
    solutionItemAccordion.style.paddingTop = "12px";
    solutionItemAccordion.templateInfos = templateInfos;
    tab.appendChild(solutionItemAccordion);
  }

  /**
   * Constructs the list of user privileges.
   *
   * @param portalUserPrivileges Privileges from a portal call
   * @returns Copy of list updated with custom privileges
   */
  private async getUserPrivileges(
    portalUserPrivileges: string[]
  ): Promise<string[]> {
    let privileges: string[] = portalUserPrivileges;

    if (this._authentication?.IsEnterprisePortal) {
      const hasNotebookServer = await this._hasNotebookServer();
      if (hasNotebookServer) {
        privileges.push(this._customNotebookServer)
      }
    }

    const velocitySupported = await helpers.checkVelocitySupport(this._userSession);
    if (!velocitySupported) {
      if (this._authentication?.PortalUserPrivileges) {
        // if velocity is not supported for the org remove the individual privileges required for the velocity template types
        const velocityPrivileges: string[] = [
          "portal:publisher:publishFeeds",
          "portal:publisher:publishBigDataAnalytics",
          "portal:publisher:publishRealTimeAnalytics"
        ];
        velocityPrivileges.forEach(p => {
          const i: number = portalUserPrivileges.indexOf(p) || -1;
          if (i !== -1) {
            portalUserPrivileges.splice(i, 1);
          }
        });
      }
    } else {
      portalUserPrivileges.push(this._customVelocity);
    }

    privileges = privileges.concat(this._getLocationTrackingPrivileges(this._getUserIsAdmin(), this._isLocationTrackingEnabled()));

    privileges = privileges.concat(this._getHubPrivileges())

    privileges = privileges.concat(await this._getWorkflowManagerPrivileges());

    return Promise.resolve(privileges);
  }

  /**
   * Adds the custom location tracking privileges to the user's privileges if the location tracking feature is enabled.
   *
   * @param userIsAdmin Whether the user is an admin
   * @param locationTrackingEnabled Whether the location tracking feature is enabled
   */
  private _getLocationTrackingPrivileges(
    userIsAdmin: boolean,
    locationTrackingEnabled: boolean
  ): string[] {
    let privileges: string[] = [];

    if (userIsAdmin && locationTrackingEnabled) {
      privileges = [this._customLocationTrackingAdminAndTracking, this._customLocationTrackingAdmin, this._customLocationTrackingTracking];
    } else if (userIsAdmin && !locationTrackingEnabled) {
      privileges = [this._customLocationTrackingAdmin];
    } else if (!userIsAdmin && locationTrackingEnabled) {
      privileges = [this._customLocationTrackingTracking];
    }

    return privileges;
  }

  /**
   * Adds the custom hub premium privilege to the user's privileges if the hub premium feature is enabled.
   */
  private _getHubPrivileges(): string[] {
    return this._isHubPremiumEnabled() ? [this._customHubPremium] : [];
  }

  /**
   * Adds the custom workflow manager privileges to the user's privileges if the workflow manager feature is enabled.
   */
  private async _getWorkflowManagerPrivileges(): Promise<string[]> {
    const baseUrl = await common.getWorkflowBaseURL(this._authentication?.RestUserSession as common.UserSession);
    const isAuthorized = await common.getWorkflowManagerAuthorized(
      baseUrl,
      this._authentication?.RestUserSession as common.UserSession
    );
    return isAuthorized ? ["solutions:workflowManager"] : [];
  }

  /**
   * Sets the metadata for when the gallery is being displayed.
   *
   * @param description Page description
   * @param title Page title
   * @private
   */
  private _setMetadataForGallery(
    description: string,
    title = "ArcGIS Solutions"
  ): void {
    // Set title and description
    document.title = title;

    this._getMetaTagByName("description")?.setAttribute("content", description);

    // Delete Open Graph tags
    ["og:title", "og:type", "og:image", "og:description", "og:url"].forEach(
      (property) => {
        const ogTag = this._getMetaTagByProperty(property);
        if (ogTag) {
          ogTag.remove();
        }
      }
    );
  }

  /**
   * Sets the metadata for when a Solution's details are being displayed.
   *
   * @param description Solution's snippet or description
   * @param title Solution's title
   * @param thumbnailUrl Solution's thumbnail's URL
   * @private
   */
  private _setMetadataForSolution(
    title: string,
    description: string,
    thumbnailUrl: string
  ): void {
    // Set title and description
    this._setMetadataForGallery(description, title + this._metadataTitleSuffix);

    // Add Open Graph tags
    this._createMetaPropertyTag("og:title", title + this._metadataTitleSuffix);
    this._createMetaPropertyTag("og:type", "website");
    this._createMetaPropertyTag("og:image", thumbnailUrl);
    this._createMetaPropertyTag("og:description", description);
    this._createMetaPropertyTag("og:url", location.href);
  }

  /**
   * Creates a meta tag with a specified property attribute and content.
   *
   * @param property Name of property attribute
   * @param content Tag's content
   * @private
   */
  private _createMetaPropertyTag(
    property: string,
    content: string
  ): void {
    const tag = document.createElement("meta");
    tag.setAttribute("property", property);  // "property" is not part of the definition of HTMLElement
    tag.content = content;
    document.head.appendChild(tag);
  }

  /**
   * Returns the first meta tag with the specified name.
   *
   * @param name Name of tag
   * @returns First match or null if none found
   * @private
   */
  private _getMetaTagByName(
    name: string
  ): HTMLElement | null {
    const matches = Array.from(document.getElementsByTagName("meta"))
      .filter((tag: any) => tag.name === name);
    return matches.length > 0 ? matches[0] : null;
  }

  /**
   * Returns the first meta tag with the specified property attribute.
   *
   * @param name Value of property
   * @returns First match or null if none found
   * @private
   */
  private _getMetaTagByProperty(
    property: string
  ): HTMLElement | null {
    const matches = Array.from(document.getElementsByTagName("meta"))
      .filter((tag: any) => tag.getAttribute("property") === property);
    return matches.length > 0 ? matches[0] : null;
  }

  /**
   * Log an event with any current trackers when the modal is closed
   * This function only logs when we are dealing with a deployment that requires user configuration to proceed.
   *
   * @private
   */
  private _trackingHandleModalClose(): void {
    if (this._trackingIsConfigure) {
      const closeType = this._trackingDidDeploy ? "deploy" : "cancel";
      const metaTitle: any = this._getMetaTagByProperty("og:title");
      const title: string = metaTitle?.content?.replace(this._metadataTitleSuffix, "");
      const action = `solution_config_${closeType}`;
      this._logEvent(
        action,
        title,
        `Solution ID: ${this._currentTrackingId}|Version: ${this._currentTrackingVersion}`
      );
    }
    this._currentTrackingId = "";
    this._currentTrackingVersion = "";
    this._trackingDidDeploy = false;
    this._trackingIsConfigure = false;
  }

  private _getUserIsAdmin(): boolean {
    const portalUserSubset = this._authentication?.PortalUserSubset;
    return portalUserSubset?.role === "org_admin" && !portalUserSubset?.hasOwnProperty("roleId");
  }

  private _isLocationTrackingEnabled(): boolean {
    return this._authentication?.PortalSubset?.locationTrackingEnabled || false;
  }

  private _isHubPremiumEnabled(): boolean {
    return this._authentication?.PortalSubset?.hubPremiumEnabled || false;
  }

  private getMaxHeights(
  ): void {
    const countContainer = document.getElementById("count-container");
    const filterContainer = document.getElementById("filter-scroll-container");
    this.elmCallback("IOPCmdRequestMaxHeights", {
      "cards": countContainer ? countContainer.offsetHeight : 32,
      "filters": filterContainer ? filterContainer.offsetTop + 12 : 240
    });
  }

  private navSelectByElement(
    clickedNode: HTMLElement
  ): void {
    const tabMenuId = clickedNode.dataset.menuId as string;
    this.navSelectByMenuId(tabMenuId);
  }

  private navSelectByName(
    tabName: string
  ): void {
    if (tabName === "home") {
      this.navSelectByMenuId("tab-1");
    } else if (tabName === "solutions") {
      this.navSelectByMenuId("tab-2");
    }
  }

  private navSelectByMenuId(
    tabMenuId: string
  ): void {
    const allNavHeaders: NodeListOf<HTMLElement> = document.querySelectorAll("[data-menu-type='nav-menu']");

    allNavHeaders.forEach((node) => {
      if (node.dataset.menuId === tabMenuId) {
        node.classList.add("is-active");

        if (this._appConfig.inGalleryMode) {
          (node.parentNode as any).setAttribute("aria-current", "true");
          const buttonLabel = document.getElementById("third-nav-btn");
          if (buttonLabel) {
            buttonLabel.innerHTML = tabMenuId === "tab-1" ? this._appConfig.pageTranslations[0] : this._appConfig.pageTranslations[1];
          }
        }
      } else {
        node.classList.remove("is-active");

        if (this._appConfig.inGalleryMode) {
          (node.parentNode as any).setAttribute("aria-current", "false");
        }
      }
    });

    const tabName = tabMenuId === "tab-1" ? "home" : "solutions";
    this.elmCallback("IOPCmdNavToggle", tabName);
  }

  private treeSelect(
    event: CustomEvent
  ): void {
    const eventTarget = event.target as any;
    if (eventTarget.id === "category-tree") {
      const selectedItems = eventTarget.selectedItems;
      let industry = "None";
      let domain = "None";
      let newRelease = "None";
      let filterType = "None";

      // Industries are the top-level items in the Industries UI tree. Some industries (e.g., Public Safety,
      // State and Local Government, Utilities) have subitems called domains (e.g., Emergency Management under
      // Public Safety).

      // If the selected items has a length of 1 this means one of two things:
      //   1. An Industry item has been selected that has no domains.
      //      In this scenario we gather the industry data attribute and
      //      assign it to the industry variable. Industry items don't have
      //      a domain data attribute so default of none will be assigned.
      //   2. A domain has been selected from within a industry.
      //      In this scenario the domain will not have an industry attribute
      //      but it will have a parentindustry and domain data attribute.
      if (selectedItems.length === 1) {
        const selectedItem = selectedItems[0];

        const hasIndustry = selectedItem.dataset.hasOwnProperty('industry');
        const hasParentIndustry = selectedItem.dataset.hasOwnProperty('parentindustry');
        const hasDomain = selectedItem.dataset.hasOwnProperty('domain');
        const hasNewRelease = selectedItem.dataset.hasOwnProperty('newRelease');

        filterType = (hasIndustry || hasParentIndustry || hasDomain) ?
          "industry-domain" : hasNewRelease ? "newRelease" : "None";

        industry = hasIndustry ? selectedItem.dataset.industry : industry;
        industry = hasParentIndustry ? selectedItem.dataset.parentindustry : industry;
        domain = hasDomain ? selectedItem.dataset.domain : domain;
        newRelease = hasNewRelease ? selectedItem.dataset.newRelease : newRelease;
      }
      // In this scenario an Industry has been selected that has multiple domains. `selectedItems` returns
      // the industry in the first position and its children (its domains) in the rest of the list.
      // The industry variable is assigned a value of its data attribute.
      // The domain variable is assigned the default value of "None", in other words
      // all of the domains are selected.
      else if (selectedItems.length > 1) {
        const selectedItem = selectedItems[0];
        if (selectedItem.dataset.hasOwnProperty('industry')) {
          industry = selectedItem.dataset.industry;
          filterType = "industry-domain";
        }
      }

      if (this._logTelemetryEvents) {
        this._currentTrackingIndustryChanged = this._currentTrackingIndustry !== industry
        this._currentTrackingIndustry = industry;
        this._currentTrackingDomainChanged = this._currentTrackingDomain !== domain;
        this._currentTrackingDomain = domain;
      }

      // if a user has clicked the tree div but hasn't selected anything then no action is taken
      this.elmCallback("IOPCmdTreeToggle", {
        industry,
        domain,
        newRelease,
        filterType
      });
    }
  }

  /**
   * Generates a short URL for the current page and copies it into the clipboard.
   */
  private placeShareLinkForSolnOntoClipboard(
  ): void {
    const self = this;
    const solutionUrl = `https://arcg.is/prod/shorten?longUrl=${encodeURIComponent(window.location.href)}`;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", solutionUrl, false);
    xhr.onload =  () => {
      try {
        const response = JSON.parse(xhr.response as string);
        if (response.status_code === 200) {
          const shortUrl: string = response.data.url;

          // Confirm copying to the clipboard
          self.confirmShare(shortUrl, () => {
            self.copyToClipboard(shortUrl)
            .then(
              () => {},
              () => {
                // Final fallback
                alert(shortUrl);
              }
            );
          });

        } else {
          // Unable to shorten URL
          this._appConfig.logger.log(LogLevel.Errors, "Could not shorten URL " + window.location.href);
        }

      } catch (err) {
        // Unable to shorten URL
        this._appConfig.logger.log(LogLevel.Errors, "Could not shorten URL " + window.location.href + ": " + err);
      }
    };

    try {
      xhr.send();
    } catch (err) {
      // Unable to shorten URL
      this._appConfig.logger.log(LogLevel.Errors, "Could not shorten URL " + window.location.href + ": " + err);
    }
  }

  /**
   * Copy a string to the clipboard.
   *
   * @param value Content to write to the clipboard
   * @returns Promise whose resolution--success or failure--indicates the success or failure of the copy
   */
  private copyToClipboard(
    value: string
  ): Promise<void> {
    // For pages served over HTTPs, we can use the navigator.clipboard
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(value);

    // Otherwise, use a textarea workaround
    } else if (document.execCommand) {
      const textHolder = document.createElement("textarea");
      textHolder.id = "copyToClipboardTextarea";
      textHolder.innerHTML = value;
      document.body.appendChild(textHolder);
      textHolder.select();
      if (document.execCommand("copy")) {
        document.body.removeChild(textHolder);
        return Promise.resolve();
      } else {
        document.body.removeChild(textHolder);
        return Promise.reject(new Error("Failed to copy to clipboard"));
      }

    } else {
      return Promise.reject(new Error("Failed to copy to clipboard"));
    }
  }

  /**
   * Shows a popup to confirm copying a sharing URL to the clipboard.
   *
   * @param shareUrl URL to display for confirmation
   * @param action Event handler to run if user confirms copying by clicking the "Copy" link
   */
  private confirmShare(
    shareUrl: string,
    action: EventListener
  ): void {
    // Disable share button
    const shareBtn = document.getElementById("shareLinkToSolutionBtn") as Element;
    if (shareBtn) {
      (shareBtn as HTMLInputElement).disabled = true;
    }

    // Create alert
    const sharingAlert = document.createElement("calcite-alert");
    sharingAlert.id = "copyToClipboardLink";
    sharingAlert.icon = "copy-to-clipboard";
    sharingAlert.placement = "top";
    sharingAlert.scale = "m";
    if (sharingAlert.messageOverrides) {
      sharingAlert.messageOverrides.close = helpers.getTranslation(this._appConfig.typescriptTranslations, "buttons.close");
    }
    sharingAlert.innerHTML =
      `<div slot='title'>${helpers.getTranslation(this._appConfig.typescriptTranslations, "alerts.copyUrlToClipboard")}</div>
      <div slot='message'>${shareUrl}</div>`;
    sharingAlert.addEventListener("calciteAlertClose",
      () => {
        document.body.removeChild(sharingAlert);
        if (shareBtn) {
          (shareBtn as HTMLInputElement).disabled = false;
        }
      }
    );

    // Create an action link to confirm the share
    const okLink = document.createElement("calcite-button");
    okLink.slot = "link";
    okLink.innerText = helpers.getTranslation(this._appConfig.typescriptTranslations, "buttons.copy");
    okLink.appearance = "transparent";
    okLink.addEventListener("click",
      (event) => {
        action(event);
        document.body.removeChild(sharingAlert);
        if (shareBtn) {
          (shareBtn as HTMLInputElement).disabled = false;
        }
      }
    );
    sharingAlert.appendChild(okLink);

    // Show the alert
    document.body.appendChild(sharingAlert);
    sharingAlert.open = true;
  }

  /**
   * Shows a popup to report that a URL was successfully copied to the clipboard.
   *
   * @param messageTag Popup's message contents
   */
  private createShareFeedback(
    messageTag: string
  ): void {
    const sharingAlert = document.createElement("calcite-alert");
    sharingAlert.autoClose = true;
    sharingAlert.autoCloseDuration = "fast";
    sharingAlert.kind = "success";
    sharingAlert.icon = true;
    sharingAlert.placement = "top-end";
    sharingAlert.scale = "s";
    sharingAlert.innerHTML =
      "<div slot='message'>" +
      helpers.getTranslation(this._appConfig.typescriptTranslations, messageTag) +
      "</div>";

    document.body.appendChild(sharingAlert);
    sharingAlert.open = true;
  }

  private createAlert(
    alertType: string,
    card: cardsHelpers.ISolutionInfoCard,
    processType: ActiveProcess,
    deployInfo?: any
  ): void {
    const createAlert: any = document.createElement("calcite-alert");
    createAlert.icon = true;
    createAlert.queue = "immediate";

    switch (alertType) {
      case "success":
        createAlert.autoClose = true;
        createAlert.kind = "success";
        const alertMsgType = processType === ActiveProcess.Deployment ? "alerts.successfullyDeployed" : "alerts.successfullyDeleted";
        createAlert.label = helpers.getTranslation(this._appConfig.typescriptTranslations, alertMsgType,
          { solutionTitle: card.title });

        const successMsgDiv = document.createElement("div");
        successMsgDiv.slot = "title";
        successMsgDiv.innerHTML = helpers.getTranslation(this._appConfig.typescriptTranslations, alertMsgType,
          { solutionTitle: card.title });

        createAlert.appendChild(successMsgDiv);
        break;

      case "failure":
        createAlert.kind = "danger";
        createAlert.label = helpers.getTranslation(this._appConfig.typescriptTranslations, "alerts.failedToDeploy",
          { solutionTitle: card.title });

        const errorMsgDiv = document.createElement("div");
        errorMsgDiv.slot = "title";
        errorMsgDiv.innerHTML = helpers.getTranslation(this._appConfig.typescriptTranslations, "alerts.failedToDeploy",
          { solutionTitle: card.title });

        const retryLink = document.createElement("calcite-action");
        retryLink.scale = "m";
        retryLink.slot = "actions-end";
        retryLink.text = helpers.getTranslation(this._appConfig.typescriptTranslations, "buttons.retry");
        retryLink.textEnabled = true;
        retryLink.addEventListener("click", (() => this.retryDeploy(card, createAlert, deployInfo)) as EventListener);

        createAlert.appendChild(errorMsgDiv);
        createAlert.appendChild(retryLink);
        break;
    }
    const action = `solution_${processType.toLowerCase()}_${alertType.toLowerCase()}`;
    this._logEvent(
      action,
      card.title,
      `Solution ID: ${card.id}|Version: ${card.deployVersion}`
    );

    // Show the alert
    document.body.appendChild(createAlert);
    createAlert.open = true;
  }

  /**
   * Create the consent manager and ask for consent when EUEI is not defined
   *
   * @param authentication Auth the current users authentication
   * @param appConfig The current application configuration
   */
  private _handleConsentManager(
    authentication: Auth | null,
    appConfig: IAppConfig
  ): void {
    const isEnterprise = authentication?.IsEnterprisePortal;

    const eueiEnabled = this._isEueiEnabled(authentication?.Portal);
    appConfig.logger.log(LogLevel.Info, `euei enabled: ${eueiEnabled}`);

    if (eueiEnabled === false || isEnterprise) {
      this._logTelemetryEvents = false;
      return;
    }

    if ((appConfig.measurementIds || appConfig.telemetry) && (appConfig.cookiesFirstUseVar || eueiEnabled)) {
      const consentManager: any = document.createElement("consent-manager");  // HTMLConsentManagerElement
      consentManager.portal = authentication?.Portal;
      consentManager.measurementIds = appConfig.measurementIds || [];
      consentManager.firstUseVar = appConfig.cookiesFirstUseVar;

      const env = this._forceTrackingEnv ||
        helpers.getEnvironment(document.location.hostname, authentication?.Portal?.portalHostname || "");

      console.log("env")
      console.log(env)
      const telemetryOptions = env && Object.keys(appConfig.telemetry).indexOf(env) > -1 ? appConfig?.telemetry[env] : undefined;
      if (telemetryOptions) {
        consentManager.amazonUserPoolID = telemetryOptions.amazon?.userPoolID;
        consentManager.amazonAppId = telemetryOptions.amazon?.app?.id;
        consentManager.amazonAppName = appConfig.telemetry?.name;
        consentManager.amazonVersion = appConfig.telemetry?.version;
      }

      if (localStorage.getItem(consentManager.firstUseVar) === "true" || eueiEnabled) {
        this._logTelemetryEvents = true;
        consentManager.classList.add("display-none");
      }

      if (!eueiEnabled) {
        // when euei is not explictly true or false we will ask the user for permission
        window.addEventListener("consentGranted", (response) => {
          const detail = (response as CustomEvent).detail;
          if (detail?.granted) {
            this._logTelemetryEvents = true;
            this.setTelemetryInstance(consentManager);
          }
        });
      } else {
        this.setTelemetryInstance(consentManager);
      }

      // needs to be added to the dom to function properly
      const appContainer = document.getElementById("appContainer");
      appContainer?.appendChild(consentManager);
    }
  }

  /**
   * Get the telemetry instance from the provided consent manager
   *
   * @param consentManager HTMLConsentManagerElement the consent manager that has been used
   */
  private setTelemetryInstance(
    consentManager: HTMLConsentManagerElement
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    consentManager.getInstance().then((i: Telemetry | undefined) => {
      this._telemetryInstance = i;
    });
  }

  /**
   * Log events using the telemetry instance
   *
   * @param action The event name
   * @param label The label for this event
   * @param details Additional details to log with the event
   */
  private _logEvent(
    action: string,
    label: string,
    details: string
  ): void {
    if (this._logTelemetryEvents) {
      this._telemetryInstance?.logEvent({
        category: "solution_deployment",
        action,
        label,
        details
      });
      this._appConfig.logger.log(LogLevel.Info, action);
      this._appConfig.logger.log(LogLevel.Info, label);
      this._appConfig.logger.log(LogLevel.Info, details);
    }
  }

  /**
   * Log events when the filter or query change.
   * This function should not log new events when the sorting changes
   *
   * @param hostname
   */
  private _handleQueryFilterTracking(
    availSolnsQuery: cardsFetcher.ISearchOptionPartial
  ): void {
    // avoid logging base query
    const baseQueries = [
      "(typekeywords:Solution,Template)",
      "(typekeywords:Solution,Display Only)",
      "(typekeywords:Solution,Template) OR (typekeywords:Solution,Display Only)",
      "((typekeywords:Solution,Deployed,'ArcGIS Solution') OR ((typekeywords:Solution,Deployed) AND (created:[1577862000000 TO 1622576880000])))"
    ];

    const newRelease = availSolnsQuery.categories?.indexOf("/Categories/New Release") > -1;
    const newReleaseChanged = this._currentTrackingNewRelease !== newRelease;

    const isBaseQuery = baseQueries.indexOf(availSolnsQuery.q.trim()) > -1;
    const suggestedForYou = availSolnsQuery.getSuggested;
    const suggestedForYouChanged = this._currentTrackingSuggestedForYou !== suggestedForYou;

    if (isBaseQuery) {
      this._currentTrackingQuery = "";
    }

    if (!isBaseQuery || newReleaseChanged || suggestedForYouChanged || this._currentTrackingDomainChanged || this._currentTrackingIndustryChanged) {
      if (!availSolnsQuery.categories || availSolnsQuery.categories.length === 0) {
        this._currentTrackingIndustry = "";
        this._currentTrackingDomain = "";
      }

      if (!isBaseQuery && availSolnsQuery.q !== this._currentTrackingQuery) {
        this._currentTrackingQuery = availSolnsQuery.q;
        this._logEvent(
          "query_solutions",
          "Query",
          `Query: ${availSolnsQuery.q}`
        );
      } else if ((newReleaseChanged && newRelease) ||
        (suggestedForYouChanged && suggestedForYou) ||
        (this._currentTrackingDomainChanged && this._currentTrackingDomain !== "") ||
        (this._currentTrackingIndustryChanged && this._currentTrackingIndustry !== "")
      ) {
        this._logEvent(
          "filter_solutions",
          `Filter`,
          `Industry: ${this._currentTrackingIndustry}|Domain: ${this._currentTrackingDomain}|New Release: ${newRelease}|Suggested For You: ${suggestedForYou}`
        );
      }
    }
    this._currentTrackingNewRelease = newRelease;
    this._currentTrackingSuggestedForYou = suggestedForYou ? true : false;
    this._currentTrackingDomainChanged = false;
    this._currentTrackingIndustryChanged = false;
  }

  /**
   * Check if Esri User Experience Improvement (EUEI) program has been enabled in the users ORG
   *
   * @param portal the current portal instance
   */
  private _isEueiEnabled(
    portal: __esri.Portal | undefined
  ): boolean | undefined {
    return portal?.eueiEnabled;
  }

  private retryDeploy(
    card: cardsHelpers.ISolutionInfoCard,
    retryAlert: HTMLCalciteAlertElement,
    deployInfo: any
  ): void {
    this._logEvent(
      "solution_deployment_retry",
      card.title,
      `Solution ID: ${card.id}|Version: ${card.deployVersion}`
    );
    retryAlert.open = false;
    this.elmCallback("IOPCmdRetryDeploy", card, deployInfo.userOptions);
  }

  private addActiveProcess(
    process: ActiveProcess
  ): void {
    switch (process) {
      case ActiveProcess.Deployment:
        this._activeDeploymentCount += 1;
        break;
      case ActiveProcess.Deletion:
        this._activeDeletionCount += 1;
        break;
      default:
        break;
    }
  }

  private removeActiveProcess(
    process: ActiveProcess
  ): void {
    // Extra check to make sure we never go below 0
    switch (process) {
      case ActiveProcess.Deployment:
        this._activeDeploymentCount -= this._activeDeploymentCount > 0 ? 1 : 0;
        break;
      case ActiveProcess.Deletion:
        this._activeDeletionCount -= this._activeDeletionCount > 0 ? 1 : 0;
        break;
      default:
        break;
    }
  }

  // Forward information to Elm module
  private elmCallback(
    command: string,
    data: any = {},
    optionalData: any = {}
  ): void {
    const response: IElmCallback = {
      command,
      data,
      optionalData
    };
    this._elmComponent?.ports.elmCallback.send(response);
  }

  private hasSufficientPrivileges(
    userPrivilegeSet: string[],
    requiredPrivilegeSet: string[]
  ): boolean {
    // Make sure that all of the required privileges are in the user's privilege set
    const missingPrivileges = requiredPrivilegeSet.filter(
      requiredPrivilege => userPrivilegeSet.indexOf(requiredPrivilege) < 0
    );
    return missingPrivileges.length === 0;
  }

  /**
   * Constructs a list of privlidges that are required for the current solution.
   *
   * @returns The list of privlidges
   */
  private getRequiredSolutionPrivileges(
    templateTypeList: string[]
  ): string[] {
    const privilegeList = [] as string[];
    // All solutions require create items
    privilegeList.push("portal:user:createItem");

    if (templateTypeList.indexOf("Group") >= 0) {
      privilegeList.push("portal:user:createGroup");
      privilegeList.push("portal:user:shareToGroup");
    }
    if (templateTypeList.indexOf("Feature Service") >= 0) {
      privilegeList.push("portal:publisher:publishFeatures");
    }

    // Velocity
    const hasFeed = templateTypeList.indexOf("Feed") > -1;
    if (hasFeed) {
      privilegeList.push("portal:publisher:publishFeeds");
    }

    const hasBigDataAnalytic = templateTypeList.indexOf("Big Data Analytic") > -1;
    if (hasBigDataAnalytic) {
      privilegeList.push("portal:publisher:publishBigDataAnalytics");
    }

    const hasRealTimeAnalytic = templateTypeList.indexOf("Real Time Analytic") > -1;
    if (hasRealTimeAnalytic) {
      privilegeList.push("portal:publisher:publishRealTimeAnalytics");
    }

    if (hasFeed || hasBigDataAnalytic || hasRealTimeAnalytic) {
      privilegeList.push(this._customVelocity);
    }

    // Workflow
    if (templateTypeList.indexOf("Workflow") > -1) {
      privilegeList.push("portal:user:createGroup");
      privilegeList.push("portal:publisher:publishFeatures");
      privilegeList.push("portal:user:shareToGroup");
      privilegeList.push("solutions:workflowManager");
    }

    // GP
    if (templateTypeList.indexOf("Geoprocessing Service") > -1) {
      privilegeList.push("portal:publisher:publishServerGPServices");
      // Notebook
      // https://devtopia.esri.com/WebGIS/solution-deployment-apps/issues/285
      // need to know if they have notbook server installed
      if (this._authentication?.IsEnterprisePortal) {
        privilegeList.push(this._customNotebookServer);
      }
    }

    return privilegeList;
  }

  private _updatePrivilegeListByGroup(
    privilegeList: string[],
    data: any
  ): void {
    if (data.templates.some((t: any) => t.type === "Group" && t.item?.membershipAccess === "collaboration")) {
      privilegeList.push("portal:user:addExternalMembersToGroup");
    }
  }

  private _updatePrivilegeListByTemplateTypeKeywords(
    privilegeList: string[],
    templateTypeKeywords: string[]
  ): void {
    // Tracking
    const trackingKeywords: string[] = ["Location Tracking View"];
    if (trackingKeywords.some(t => templateTypeKeywords.indexOf(t) > -1)) {
      const userIsAdmin: boolean = this._getUserIsAdmin();
      const locationTrackingEnabled: boolean = this._isLocationTrackingEnabled();
      if (!userIsAdmin && !locationTrackingEnabled) {
        privilegeList.push(this._customLocationTrackingAdminAndTracking);
      } else if (userIsAdmin && !locationTrackingEnabled) {
        privilegeList.push(this._customLocationTrackingTracking);
      } else if (!userIsAdmin && locationTrackingEnabled) {
        privilegeList.push(this._customLocationTrackingAdmin);
      }
    }
  }

  private _updatePrivilegeListBySolutionTypeKeywords(
    privilegeList: string[],
    solutionTypeKeywords: string[]
  ): void {
    // Hub
    const hubKeywords: string[] = ["HubPremium"];
    if (hubKeywords.some(t => solutionTypeKeywords.indexOf(t) > -1)) {
      privilegeList.push(this._customHubPremium);
    }
  }

  private formatParams(
    params: common.IStringValuePair
  ): Promise<IUserOption[]> {
    const userOptions: IUserOption[] = [];
    const paramKeys: string[] = params ? Object.keys(params) : [];

    // if we ever need multiple pages...elm side will still support
    // each UserOption would get a unique page
    // for now just moving userOption def outside the loop to create a single page
    const userOption: IUserOption = {
      id: "",
      props: [],
      accordionDisplay: false
    };
    if (paramKeys.length > 0) {
      const existingItemDefs: Array<Promise<any>> = [];
      paramKeys.forEach(id => {
        const p = params[id];
        if (p.valueType && p.valueType.toLowerCase() === "itemid") {
          existingItemDefs.push(
            this._findExistingItem(id, "Feature Service", this._userSession?.username || "")
          );
        }
      });

      return Promise.all(existingItemDefs).then(results => {
        const idHash: any = {};
        results.forEach(r => {
          if (r.query && r.results && r.results.length > 0) {
            const sourceId: string = r.query.split(" ")[0].replace("typekeywords:source-", "");
            idHash[sourceId] = {
              resultId: r.results[0].id,
              response: r.results[0]
            };
          }
        });

        paramKeys.forEach(id => {
          // custom item id params support an optional user defined learn more link
          // this link will show as a part of a high level set of descriptive info
          // and does not make sense to store with each individual param
          if (id === "serviceLearnMore") {
            this._configureLearnMore = params[id];
            delete (params[id]);
            return;
          }
          // if the param contains enterpriseIDMapping cache here and pass back for deployment
          // no need for this to pass through the elm side as it is not used for any aspects of the UI
          if (params[id].enterpriseIDMapping) {
            this._customDeploymentMappings[id] = params[id].enterpriseIDMapping;
          }
          userOption.id = id;
          if (idHash[id]) {
            params[id].existingItemId = idHash[id].resultId;
            this.updateUserProp(params[id] as IUserProp, idHash[id].response as common.IStringValuePair);
          }
          this.updateOption(params[id] as common.IStringValuePair, userOption, id);
        });
        userOptions.push(userOption);

        return Promise.resolve(userOptions);

      }).catch(() => Promise.resolve([]));
    } else {
      return Promise.resolve([]);
    }
  }

  /**
   * Check if the portal has NotebookServer installed
   *
   * @returns A promise with a boolean value. When true NotebookServer is installed.
   */
  private async _hasNotebookServer(): Promise<boolean> {
    const portal = this._portalUrl + "/sharing/rest";
    const requestParams: request.IRequestOptions = {
      authentication: this._userSession as common.UserSession,
      portal,
      params: {
        f: "json"
      }
    };
    return request.request(`${portal}/portals/self/servers`, requestParams).then((response: common.IStringValuePair) => {
      return response?.servers?.find((s: any) => s.serverFunction.indexOf("NotebookServer") > -1);
    }, (e) => {
      console.error(e);
      return false;
    });
  }

  private updateOption(
    v: common.IStringValuePair,
    userOption: IUserOption,
    id: string
  ): void {
    const defValue: string = v.default || "";
    const outType: string = typeof (defValue);
    const defaultValue: string = defValue.toString();
    this._setFeatureServiceProps(v);
    const validation: any = this.getValidation(v, defaultValue);
    userOption.props.push({
      id,
      outType,
      prop: id,
      itemType: v.itemType || "",
      responseValue: v.responseValue || "",
      label: v.label || "",
      choices: v.choices || [],
      valueType: v.valueType.toLowerCase(),
      attributes: v.attributes || {},
      value: v.existingItemId && v.valueType.toLowerCase() === "itemid" ? v.existingItemId : defaultValue,
      default: defaultValue,
      srSearchText: defaultValue,
      autoSelect: defaultValue !== "",
      ...validation,
      loading: false,
      validId: true,
      tooltip: v.tooltip || "",
      name: v.name || "",
      title: v.title || "",
      itemUrl: v.itemUrl || ""
    } as IUserProp);
  }

  private _findExistingItem(
    itemId: string,
    type: string,
    userName: string
  ): Promise<any> {
    // the higher level function is based on the idea of starting with templates
    // I may move this to a common function is solution.js
    return deployer._findExistingItem(
      `typekeywords:source-${itemId} type:${type} owner:${userName}`,
      this._userSession as common.UserSession
    );
  }

  private _setFeatureServiceProps(
    v: any
  ): void {
    if (v.valueType && v.valueType.toLowerCase() === "itemid") {
      common.setCreateProp(v, "attributes.required", "true");
    }
  }

  // set the inital valid state when a value is required
  private getValidation(
    v: common.IStringValuePair,
    defaultValue: string
  ): any {
    let expression: string = "";
    let message: string = common.getProp(v, "validation.message") || "";
    let valid: boolean = true;

    if (common.getProp(v, "attributes.required") === "true") {
      expression = common.getPropWithDefault(v, "validation.expression", "\\S{1,}");
      const regEx = new RegExp(expression);
      valid = regEx.test(defaultValue);
    }

    // itemid validation options are defined by default
    if (v.valueType && v.valueType.toLowerCase() === "itemid") {
      expression = this._regexID;
      message = helpers.getTranslation(this._appConfig.elmTranslations, "configure.invalidID");
    }

    return {
      valid,
      validation: {
        expression,
        message
      }
    };
  }

  private validateID(
    data: any
  ): void {
    const v: string = data.userProp.value.trim();
    const idTest: RegExp = new RegExp(this._regexID, "i");
    if (idTest.test(v)) {
      this._validate(`${this._portalUrl}/sharing/rest/content/items/${v}`, data.userProp as IUserProp);
    } else {
      data.userProp.validId = false;
      data.userProp.responseValue = "";
      data.userProp.name = "";
      data.userProp.title = "";
      data.userProp.itemUrl = "";
      this.elmCallback("IOPCmdValidateID", data.userProp.validId, data.userProp);
    }
  }

  private _validate(
    url: string,
    userProp: IUserProp
  ): void {
    const requestParams: request.IRequestOptions = {
      authentication: this._userSession as common.UserSession,
      portal: this._portalUrl + "/sharing/rest",
      params: {
        f: "json"
      }
    };
    request.request(url, requestParams).then((response: common.IStringValuePair) => {
      const validProp = this.updateUserProp(userProp, response);
      this.elmCallback("IOPCmdValidateID", validProp, userProp);
    }, () => {
      userProp.validId = false;
      userProp.responseValue = "";
      userProp.name = "";
      userProp.title = "";
      userProp.itemUrl = "";
      this.elmCallback("IOPCmdValidateID", userProp.validId, userProp);
    });
  }

  private updateUserProp(
    userProp: IUserProp,
    response: common.IStringValuePair
  ): boolean {
    const isLayer: boolean = userProp.itemType.toLowerCase() === "feature service";

    const responseTest: RegExp = new RegExp(isLayer ? this._regexFsUrl : userProp.itemType, "i");
    const responseProp: string = common.getPropWithDefault(response, isLayer ? "url" : "type", "") || "";

    const validProp: boolean = responseProp !== "" && responseTest.test(responseProp);
    userProp.responseValue = validProp ? responseProp : "";
    userProp.name = validProp ? common.getProp(response, "name") || "" : "";
    userProp.title = validProp ? common.getProp(response, "title") || "" : "";
    // update to make sure case matches..
    userProp.itemType = validProp ? common.getProp(response, "type") : userProp.itemType;
    userProp.validId = validProp;
    userProp.itemUrl = this._getItemUrl(response.id as string);
    return validProp;
  }

  //#endregion
  // ---------------------------------------------------------------------------------------------------------------- //
  //#region elmRequestHandler functions

  /**
   * Fetches cards for available Solutions from the server.
   *
   * @param command Command that triggered this query
   * @param query AGO query terms and options
   * @param primarySolutionsGroupId Id of a group that's the primary source of Solutions for AGO or Enterprise
   * @param agoBasedEnterpriseSolutionsGroupId Id of an AGO-based set of Enterprise Solutions
   * @param pagingParams Parameters for how the request is to be batched
   */
  private requestAvailableSolutionCards(
    command: string,
    query: cardsFetcher.ISearchOptionPartial,
    primarySolutionsGroupId: string,
    agoBasedEnterpriseSolutionsGroupId: string,
    pagingParams?: restTypes.IPagingParams
  ): void {
    if (this._authentication?.IsEnterprisePortal && !primarySolutionsGroupId && !agoBasedEnterpriseSolutionsGroupId) {
      // Nothing to search
      this.elmCallback(command, this._appConfig.typescriptTranslations.alerts.enterpriseCannotAccessAGO);
      return;
    }

    if (this._logTelemetryEvents) {
      this._handleQueryFilterTracking(query);
    }

    /*
    // Save the query in case we want to refresh the home page
    this._availableSolutionCardsLastQuery = {
      command,
      query,
      primarySolutionsGroupId,
      agoBasedEnterpriseSolutionsGroupId,
      pagingParams
    }
    */

    this._cardsFetcherAvailable.requestAvailableSolutionCards(query, primarySolutionsGroupId,
      agoBasedEnterpriseSolutionsGroupId, this._userSession, pagingParams,
      (query.getSuggested ? this._relatedSolutions : undefined))
      .then(response => {
        // Convert the related solution ids in each card to item ids
        response.cards = response.cards.map(
          card => {
            if (Array.isArray(card.relatedSolutions) && card.relatedSolutions.length > 0) {
              card.relatedSolutions =
                cardsHelpers.convertSolutionIdsToItemIds(card.relatedSolutions, this._solutionIdMappingToItemId);
            }
            return card;
          }
        );

        // Send the cards to the UI
        this.elmCallback(
          command,         // command
          response.cards,  // data
          response.paging  // optional data
        );
      })
      .catch((error: string) => {
        const errorMsg =
          this._authentication?.IsEnterprisePortal && (!primarySolutionsGroupId || error === "enterpriseCannotAccessAGO")
          ? this._appConfig.typescriptTranslations.alerts.enterpriseCannotAccessAGO
          : error
        this.elmCallback(
          "IOPCmdRequestAvailableSolutionsError",  // command
          errorMsg                                 // data
        );
      });
  }

  /**
   * Fetches cards for deployed Solutions from the server.
   *
   * @param command Command that triggered this query
   * @param query AGO query terms and options
   * @returns Promise when task is done
   */
  private requestDeployedSolutionCards(
    command: string,
    query: cardsFetcher.ISearchOptionPartial
  ): Promise<void> {
    return this._cardsFetcherDeployed.requestDeployedSolutionCards(query, this._userSession)
      .then(
        response => {
          // Update the list of related solutions
          this._relatedSolutions = cardsHelpers.getDeployedRelatedSolutionIds(response.cards, this._allCards);

          // Send the cards to the UI
          if (command) {
            this.elmCallback(
              command,
              response.cards,
              this._relatedSolutions.size > 0
            );
          }
        },
        error => {
          if (command) {
            this.elmCallback(command, error.toString());
          }
        }
      );
  }

  private deploySolution(
    command: string,
    deployInfo: IRequestDeploySolution,
    userSession: common.UserSession
  ): void {
    if (!userSession) {
      this.elmCallback(command, "Authentication required for creating a group");
      return;
    }

    // Deploy a solution described by the supplied id
    const options: common.IDeploySolutionOptions = {
      progressCallback: (percentDone, jobId) => {  // progress callback
        this.elmCallback("IOPNotifProgressUpdate", {
          id: jobId,
          percentage: Math.round(percentDone)
        });
      },
    };

    this.addActiveProcess(ActiveProcess.Deployment);

    options.enableItemReuse = deployInfo.reuse;
    options.jobId = deployInfo.jobId;
    options.templateDictionary = {
      params: this.getUserParams(deployInfo),
      workflowURL: this._authentication?.PortalSubset?.workflowURL || ""
    };

    // Set source user session of the solution
    // Use anonymous user session if solution is part "connected" enterprise group
    options.storageAuthentication = cardsHelpers.getCardSession(deployInfo.card, userSession, this._anonAgolSession);

    deployer.deploySolution(deployInfo.card.id, userSession, options).then(
      newSolutionId => {
        // Convert item into a solution card
        common.getItemBase(newSolutionId, userSession).then(
          (newSolution: common.IItem) => {
            const card: cardsHelpers.ISolutionInfoCard = {
              id: newSolution.id,
              title: newSolution.title,
              snippet: newSolution.snippet || "",
              description: newSolution.description || "",
              url: newSolution.url || "",
              itemUrl: this._getItemUrl(newSolution.id),
              thumbnailFilename: newSolution.thumbnail,
              thumbnailUrl: helpers.generateThumbnailUrl(newSolution.id, newSolution.thumbnail as string,
                deployInfo.card.thumbnailUrl, this._portalUrl),
              tryitUrl: newSolution.tryitUrl || deployInfo.card.tryitUrl || "",
              typeKeywords: newSolution.typeKeywords || [],
              learnMoreUrl: newSolution.properties?.learnMoreUrl || "",
              createdMs: newSolution.created,
              created: helpers.getLocaleLongDateString(newSolution.created, this._appConfig.locale),
              modified: helpers.getLocaleLongDateString(newSolution.modified, this._appConfig.locale),
              numViews: newSolution.numViews,
              tags: newSolution.tags,
              categories: newSolution.categories || [],
              licenseInfo: helpers.createDivHtml(newSolution.licenseInfo as string),
              deployCommonId: helpers.getTypeKeywordProp(newSolution.typeKeywords || [] as string[], "solutionid-"),
              deployVersion: helpers.getTypeKeywordProp(newSolution.typeKeywords || [] as string[], "solutionversion-"),
              canDelete: newSolution.created > 1618331400000, // Only solutions deployed after April 13, 2021 at 9:30PM can be deleted (9.1 release)
              canDeploy: (newSolution.typeKeywords || []).indexOf("Display Only") < 0,
              preview: [],
              sourcePortalUrl: "",
              knownToBePublicSource: false,
              waitId: deployInfo.card.waitId,
              relatedSolutions: common.getProp(newSolution.properties as { [index: string]: any }, "relatedSolutions") as string[] || null
            };

            this.removeActiveProcess(ActiveProcess.Deployment);
            this.createAlert("success", card, ActiveProcess.Deployment);
            this.elmCallback(command, { "card": card, "jobId": options.jobId });

            // Update the list of related solutions after adding the new card to the cache
            this._cardsFetcherDeployed.addToCache(card);
            this._relatedSolutions = cardsHelpers.updateDeployedRelatedSolutionIds(card, this._allCards, this._relatedSolutions);
            this.elmCallback("IOPCmdUpdateRelatedSolutions", null, this._relatedSolutions.size > 0);

            this.elmCallback("IOPNotifProgressUpdate", {
              id: deployInfo.card.id,
              percentage: 0
            });
          },
          error => {
            this._appConfig.logger.log(LogLevel.Errors, error);
            this.removeActiveProcess(ActiveProcess.Deployment);
            this.createAlert("failure", deployInfo.card, ActiveProcess.Deployment, deployInfo);
            this.elmCallback("IOPCmdDeployFailure", { "card": deployInfo.card, "jobId": options.jobId });
          }
        );
      },
      error => {
        this._appConfig.logger.log(LogLevel.Errors, error);
        this.removeActiveProcess(ActiveProcess.Deployment);
        this.createAlert("failure", deployInfo.card, ActiveProcess.Deployment, deployInfo);
        this.elmCallback("IOPCmdDeployFailure", { "card": deployInfo.card, "jobId": options.jobId });
      }
    );
  }

  /**
   * Generates the URL to an item.
   *
   * @param id Id of item to use in URL
   * @return URL
   */
  private _getItemUrl(
    id: string
  ): string {
    return this._orgUrl + "/home/item.html" + helpers.addParams([{ k: "id", v: id }]) || "";
  }

  //#endregion
  // ---------------------------------------------------------------------------------------------------------------- //
  //#region Support functions

  /**
   * Generates a string containing the organization id and/or the organization owner if that information is
   * available in the authentication.
   *
   * @param auth Authentication
   * @returns String with key/value pairs for org id and/or org owner; string is prefixed with a space so that it
   * can be added to an AGO query; empty string returnd if neither property is available
   */
  private getOrgAndOwnerQueryPart(
    auth: Auth
  ): string {
    let suffix = "";

    const sourceOrgId = auth.PortalUserOrg;
    const sourceOrgOwner = auth.PortalUserName;
    if (sourceOrgId) {
      suffix += " orgid:" + sourceOrgId;  // TODO: restore org in query
    }
    if (sourceOrgOwner) {
      suffix += " owner:" + sourceOrgOwner;  // TODO: restore org in query
    }

    return suffix;
  }

  /**
   * Sets the focus in a modal.
   *
   * @param modal Modal to modify
   * @param focusType Indicates if focus should be set to the modal title ("title") or to its content (any other
   * value)
   */
  private focusModalContent(
    modal: HTMLCalciteModalElement,
    focusType?: string
  ): void {
    modal.open = true;

    let modalFocusElement: HTMLElement;
    if (focusType === "title") {
      modalFocusElement = document.getElementById("solutions-modal-title") as HTMLElement;
    }
    else {
      modalFocusElement = document.getElementById("solutions-modal-content") as HTMLElement;
    }
    // Add Tab Index so content is focusable
    modalFocusElement.tabIndex = 0;
    // Remove outline because we dont want a black box
    // Inline styling because element gets created in shadow root
    modalFocusElement.style.outline = "none";

    // Need to lag a bit before setting focus
    setTimeout(
      () => {
        modalFocusElement.focus();
        void modal.scrollContent();
      }, 200
    );
  }

  /**
   * Builds the mapping from solution ids to item ids.
   *
   * @param cards Cards to use for mapping from solution id to item id
   * @returns Set with solution ids as keys and the corresponding item ids as values
   */
  private buildSolutionIdMappingToItemId(
    cards: cardsHelpers.ISolutionInfoCard[]
  ): cardsHelpers.IKeyedStrings {
    // Create a mapping from solution ids to item ids
    const solutionIdPrefix = "solutionid-";
    const solutionIdMappingToItemId: cardsHelpers.IKeyedStrings = {};

    cards.forEach(
      (card) => {
        const solutionId = card.typeKeywords.filter(
          (typeKeyword) => typeKeyword.startsWith(solutionIdPrefix)
        );
        if (solutionId.length !== 1) {
          this._appConfig.logger.log(LogLevel.Warnings, "Solution " + card.id + " (" + card.title + ") has the wrong number of solutionids: ",
            JSON.stringify(card.typeKeywords, null, 2));
        } else {
          solutionIdMappingToItemId[solutionId[0].substring(solutionIdPrefix.length)] = card.id;
        }
      }
    );

    return solutionIdMappingToItemId;
  }

  private getUserParams(
    deployInfo: IRequestDeploySolution
  ): any {
    // get any user defined params to pass to deploy
    const params: any = {};
    const userOptions: any = common.getProp(deployInfo, "userOptions");
    if (userOptions) {
      userOptions.forEach((option: IUserOption) => {
        option.props.forEach((prop: IUserProp) => {
          if (prop.prop === "wkid") {
            // we need to pass the SRs default extent
            // this will help avoid situations where the orgs default extent and default world extent
            // will not project successfully to the chosen spatial reference
            const defaultExtent =
              helpers.getDefaultExtent(parseInt(prop.value, 10), this._spatialReferences.wkids, prop.default);
            if (defaultExtent) {
              params.defaultExtent = JSON.parse(defaultExtent);
              params.defaultExtent.spatialReference = { wkid: parseInt(prop.value, 10) };
            }
          }
          if (prop.value !== "") {
            if (prop.valueType === "itemid") {
              params.useExisting = true;
              // FS stores the items url as the response value.
              // This is used to determine if the itemId is valid for this type
              // Other item types do not necessarily have a url so the item type is stored as the response value.
              // We simply check if the item is of the type defined in the solutions params.
              params[prop.prop] = {
                itemId: prop.value,
                url: prop.responseValue !== prop.itemType ? prop.responseValue : "",
                name: prop.name,
                title: prop.title,
                type: prop.itemType,
                enterpriseIDMapping: this._customDeploymentMappings[prop.prop] || {}
              };
            } else {
              params[prop.prop] = prop.outType === "number" ? parseInt(prop.value, 10) : prop.value;
            }
          } else if (prop.default !== "") {
            params[prop.prop] = prop.default;
          }
        });
      });
    }
    return params;
  }

  /**
   * Sets the aria-current attribute on the Home and My Solutions tabs to highlight the active one.
   *
   * @param setHomeAsActive If true, set the Home tab active and the My Solutions tab inactive
   */
  private _setActiveTertiaryMenuTab(
    setHomeAsActive: boolean
  ): void {
    const home = document.getElementById("home-submenu-link");
    if (home) {
      const homeActive = setHomeAsActive ? "true" : "false";
      home.setAttribute("aria-current", homeActive);
      home.parentElement?.setAttribute("aria-current", homeActive);
    }

    const solutions = document.getElementById("solutions-submenu-link");
    if (solutions) {
      const solutionsActive = setHomeAsActive ? "false" : "true";
      solutions.setAttribute("aria-current", solutionsActive);
      solutions.parentElement?.setAttribute("aria-current", solutionsActive);
    }
  }

  //#endregion
  // ---------------------------------------------------------------------------------------------------------------- //

}
